<section class="splash" aria-labelledby="splashTitle">
  <app-star-field></app-star-field>
  <div class="splash__hero">
    <div class="splash__intro">
      <p class="splash__eyebrow">Spark Business Technology</p>
      <h1 id="splashTitle">Server Framework</h1>
      <p class="splash__lead">
        A high-fidelity Node.js API runtime where Express routing, WebSockets, Sequelize, and OpenAPI 3.1 documentation
        are all
        driven directly from strongly typed TypeScript.
      </p>
      <div class="splash__actions">
        <a routerLink="/login" class="btn btn--primary">Launch Console</a>
        <a routerLink="/account/register" class="btn btn--ghost">Get Access</a>
        <a routerLink="/licensing" class="btn btn--ghost" aria-label="Read how Spark approaches licensing">
          How We Approach Licensing
        </a>
      </div>
      <div class="splash__scroll">Scroll to explore</div>
    </div>
  </div>

  <div class="splash__hero-grid" aria-label="Platform highlights and signals">
    <div class="splash__callouts">
      <div class="splash__callouts-header">
        <div class="splash__meta">
          <div>
            <span class="pill">npm</span>
            <code>@spark-sdk/server-framework</code>
          </div>
          <p>Typed controllers · Declarative docs · CI-native migrations</p>
        </div>
        <p class="splash__callouts-label">Built for teams who need</p>
      </div>
      <h2 class="sr-only" id="calloutsHeading">Platform capabilities</h2>
      <div class="splash__callouts-grid">
        <article class="splash__callout-card">
          <h3>Live contracts</h3>
          <p>Routes, WebSockets, and docs share one source of truth so changes stay in lockstep.</p>
        </article>
        <article class="splash__callout-card">
          <h3>Realtime primitives</h3>
          <p>Decorators expose REST, WebSocket, and shared SSE streams from the exact same TypeScript contract.</p>
        </article>
        <article class="splash__callout-card">
          <h3>Security First Approach</h3>
          <p>SparkServer wires helmet, compression, JSON parsing, CORS, and static spec hosting before routes mount.</p>
        </article>
        <article class="splash__callout-card">
          <h3>Self-Documenting Code</h3>
          <p>The `build` command emits OpenAPI 3.1 JSON and human readable HTML straight from controller metadata.</p>
        </article>
      </div>
    </div>

    <aside class="splash__codecard" aria-label="Install command">
      <header>
        <span>Install</span>
        <span class="status-dot"></span>
      </header>
      <pre><code class="language-bash">npm install @spark-sdk/server-framework
yarn add @spark-sdk/server-framework</code></pre>
      <p>Framework + CLI + OpenAPI tooling in one package.</p>
    </aside>
  </div>
</section>

<section class="splash-story" aria-label="Framework pillars">
  <div class="splash-story__intro">
    <h2>Purpose-built runtime for typed services.</h2>
    <p>
      Every panel below maps directly to modules inside <code>@spark-sdk/server-framework</code>: the decorators,
      helpers, and CLI that generate and host Spark APIs.
    </p>
  </div>
  <div class="splash-story__grid">
    <article>
      <h3>Dynamic controllers</h3>
      <p>`@controller`, `@read`, `@create`, and `socket`/`stream` decorators register Express + WS/SSE routes to
        listeners.
      </p>
    </article>
    <article>
      <h3>Request context</h3>
      <p>`ContextAwareService` instances autowire per-request dependencies, keeping controllers thin but stateful.</p>
    </article>
    <article>
      <h3>Sequelize + Umzug</h3>
      <p>`SparkDatabase` authenticates `sequelize-typescript`, then drives Umzug migrations before syncing models.</p>
    </article>
    <article>
      <h3>Error discipline</h3>
      <p>`defineError` and reporters keep every failure payload predictable.</p>
    </article>
    <article>
      <h3>Operational logging</h3>
      <p>`logger`and `logSignature` surface structured diagnostics for every request.</p>
    </article>
    <article>
      <h3>Consistent errors</h3>
      <p>`defineError` keeps every response payload and status predictable for clients.</p>
    </article>
    <article>
      <h3>Request-scoped services</h3>
      <p>ContextAwareService autowires per-request dependencies so controllers stay lean and composable.</p>
    </article>
    <article>
      <h3>Sequelize guardrails</h3>
      <p>`CrudService` validates filters with io-ts and safely symbolises operators before Sequelize runs queries.</p>
    </article>
  </div>
</section>

<section class="splash-panels" aria-label="Scroll showcase">
  <article class="panel">
    <div class="panel__content">
      <p class="panel__eyebrow">Bootstrap</p>
      <h3>SparkServer wires the stack for you.</h3>
      <p>
        `SparkServer` enables CORS, JSON parsing, compression, helmet, shared SSE streams, and static spec hosting
        before your
        controllers ever run—no ad hoc middleware glue.
      </p>
    </div>
    <div class="panel__code">
      <pre><code class="language-ts">import &#123; SparkServer &#125; from '@spark-sdk/server-framework';
import &#123; Sequelize &#125; from 'sequelize-typescript';

export const server = new SparkServer(&#123;
  controllers: [HealthController, AuthController],
  express: &#123; port: 4000, basePath: '/api' &#125;,
  database: &#123;
    sequelize: Sequelize,
    database: process.env.DB_NAME,
    username: process.env.DB_USER,
    password: process.env.DB_PASS,
    modelPaths: [__dirname + '/model/**/*.model.*'],
  &#125;,
&#125;);

server.start();</code></pre>
    </div>
  </article>
  <article class="panel">
    <div class="panel__content">
      <p class="panel__eyebrow">Contracts</p>
      <h3>Route builders that emit types.</h3>
      <p>
        Define validators once with `buildRoute`. Get consistent query, body, route, and outputs with strongly-typed
        request context with `ctx: typeof listRoute.ctx`.
      </p>
      <p>
        The snippet on the right mirrors how production controllers keep input/output schemas, services, and runtime
        handlers in
        sync without hand-written interfaces.
      </p>
    </div>
    <div class="panel__code">
      <pre><code class="language-ts">const listRoute = buildRoute(&#123;
  summary: `List Records`,
  queryValidator: t.type(&#123; page: t.number, pageSize: t.number, &#125;),
  bodyValidator: t.type(&#123; filter: t.string &#125;)
  outputValidator: t.type(&#123; records: t.array(...), &#125;)
  services: [ ModelService ],
&#125;);

@read(listRoute.config)
export class RecordController &#123;
  static async list(ctx: typeof listRoute.ctx) &#123;
    // Guaranteed to be a string. Available in intellisense.
    const filter = ctx.req.query.filter;
    const records = await ctx.services.model.listRecords(...);
    return &#123; records &#125;;
  &#125;
&#125;</code></pre>
    </div>
  </article>
  <article class="panel">
    <div class="panel__content">
      <p class="panel__eyebrow">Documentation</p>
      <h3>CLI that emits OpenAPI + Redoc.</h3>
      <p>
        `npx spark-server-framework document build` writes `spec.json`, and bundles `spec.html` with `redoc-cli` so your API Documentation never drifts from the deployed build.
      </p>
    </div>
    <div class="panel__code">
      <pre><code class="language-bash">$ npx spark-server-framework build
✔ controllers ↔ spec synced
✔ spec.json + spec.html in /src/static
→ ready for CDN + edge cache rollout or static serve from the API</code></pre>
    </div>
  </article>
  <article class="panel">
    <div class="panel__content">
      <p class="panel__eyebrow">Typed middleware</p>
      <h3>Middleware that upgrades context types.</h3>
      <p>
        Typed middleware mutates `ctx.req` twice: it enforces guards at runtime and widens the compile-time type so
        IntelliSense
        knows exactly which properties now exist on the request object.
      </p>
      <p>
        Stack multiple layers for admins, super-admins, or bespoke actors and the resulting `ctx` tracks each enrichment
        without
        hand-maintained interfaces.
      </p>
    </div>
    <div class="panel__code">
      <pre><code class="language-ts">
buildRoute(opts).attachMiddleware&lt;AuthenticatedRequest&gt;(superAdminMiddleware);
      </code></pre>
    </div>
  </article>
</section>

<section class="splash-terminal" aria-label="Runtime feed">
  <div class="terminal">
    <header>
      <span class="traffic light-red"></span>
      <span class="traffic light-yellow"></span>
      <span class="traffic light-green"></span>
      <p>CI Native Approach</p>
    </header>
    <pre><code class="language-bash">➜ npx spark-server-framework build
info: SparkSDK.Build: Using ServerFile /dev/project/src/main.ts (derived from project configuration).
info: SparkSDK.Build: Executing build command
info: SparkSDK.Build: Build Complete
info: SparkSDK.Build: Generating documentation via compiled package entr
debug: SparkSDK.Document: Document command running in ESM mode.
info: getDynamicControllerSpec: Documented Method[get] on Route[/health]
info: getDynamicControllerSpec: Documented Method[post] on Route[/example]
info: getDynamicControllerSpec: Documented Method[delete] on Route[/example/:id]
info: getDynamicControllerSpec: Documented Method[put] on Route[/example/:id]
info: getDynamicControllerSpec: Documented Method[get] on Route[/example]
info: getDynamicControllerSpec: Documented Method[get] on Route[/example/:id]
info: SparkSDK.Build: Documentation Complete.</code></pre>
  </div>
  <div class="terminal__meta">
    <h3>Structured diagnostics</h3>
    <p>
      Logs are emitted through Winston with exposed `logger` and enrighed with `logSignature`, encouraging consistent, structured, and easily parsed log
      output.
    </p>
    <ul>
      <li>`logSignature` prepends module, function names and metadata to log entries</li>
      <li>`logger` exposes configurable log levels and transports</li>
      <li>`reporter` surfaces payload validation failures</li>
    </ul>
  </div>
</section>

<section class="splash-case">
  <div>
    <p class="splash__eyebrow">Production Ready</p>
    <h2>Powering high-volume textile commerce.</h2>
    <p>
      We believe in dogfooding. Applications built by Spark Business Technology run on Spark Server Framework to keep
      fidelity high, performance tight, security uncompromised, and APIs well-defined with static types from day one.
      Spark Server Framework powers production-ready, high-volume custom e-commerce deployments every day.
    </p>
  </div>
  <div class="splash-case__stats">
    <div>
      <p class="stat">40+</p>
      <span>HTTP + WebSocket controllers orchestrating catalogs, carts, and approvals.</span>
    </div>
    <div>
      <p class="stat">15</p>
      <span>Automated jobs handling price syncs, inventory, and textile procurement windows.</span>
    </div>
    <div>
      <p class="stat">8</p>
      <span>Dedicated services for carts, branding, allocations, and textile-specific micrologic.</span>
    </div>
    <div>
      <p class="stat">0</p>
      <span>Custom Swagger files—OpenAPI is generated straight from controllers.</span>
    </div>
  </div>
</section>
